import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase/firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import api from '../api/api';
import { notify } from '../utils/notifications';
import '../styles/UserProfile.css';

export const UserProfile = () => {
    const { userProfile } = useAuth();

    const [name, setName] = useState(userProfile?.name || '');
    const [organization, setOrganization] = useState(userProfile?.organization || '');
    const [saving, setSaving] = useState(false);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    const [facilityConfig, setFacilityConfig] = useState(null);

    useEffect(() => {
        const facilityType = userProfile?.facility_type;
        if (!facilityType) return;
        api.get(`/admin/facility-configs/${facilityType}`)
            .then((resp) => setFacilityConfig(resp.data))
            .catch(() => setFacilityConfig(null));
    }, [userProfile?.facility_type]);

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put('/user/profile', {
                name: name.trim(),
                organization: organization.trim(),
            });
            // Profile updates reactively via Firestore onSnapshot
            notify.success('Profile updated.');
        } catch (err) {
            notify.error('Failed to update profile.');
        } finally {
            setSaving(false);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');

        if (newPassword !== confirmNewPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }

        if (newPassword.length < 6) {
            setPasswordError('New password must be at least 6 characters.');
            return;
        }

        setChangingPassword(true);
        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);

            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            notify.success('Password changed successfully.');
        } catch (err) {
            if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
                setPasswordError('Current password is incorrect.');
            } else {
                setPasswordError('Failed to change password. Please try again.');
            }
        } finally {
            setChangingPassword(false);
        }
    };

    return (
        <div className="profile-container">
            <h2>User Profile</h2>

            <div className="profile-section">
                <h3>Personal Information</h3>
                <form onSubmit={handleSaveProfile}>
                    <div className="profile-field">
                        <label htmlFor="profileName">Name:</label>
                        <input
                            id="profileName"
                            type="text"
                            className="profile-input"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="profileEmail">Email:</label>
                        <input
                            id="profileEmail"
                            type="email"
                            className="profile-input"
                            value={userProfile?.email || ''}
                            disabled
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="profileOrg">Organization:</label>
                        <input
                            id="profileOrg"
                            type="text"
                            className="profile-input"
                            value={organization}
                            onChange={(e) => setOrganization(e.target.value)}
                        />
                    </div>
                    <button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </form>

                {userProfile?.facility_type && (
                    <div className="profile-facility">
                        <label>Facility Workflow:</label>
                        <div className="profile-facility-info">
                            <span className="profile-facility-name">
                                {facilityConfig?.name || userProfile.facility_type}
                            </span>
                            {facilityConfig?.description && (
                                <span className="profile-facility-desc">{facilityConfig.description}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="profile-section">
                <h3>Change Password</h3>
                <form onSubmit={handleChangePassword}>
                    {passwordError && <div className="error">{passwordError}</div>}
                    <div className="profile-field">
                        <label htmlFor="currentPwd">Current Password:</label>
                        <input
                            id="currentPwd"
                            type="password"
                            className="profile-input"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="newPwd">New Password:</label>
                        <input
                            id="newPwd"
                            type="password"
                            className="profile-input"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={6}
                            autoComplete="new-password"
                        />
                    </div>
                    <div className="profile-field">
                        <label htmlFor="confirmNewPwd">Confirm New Password:</label>
                        <input
                            id="confirmNewPwd"
                            type="password"
                            className="profile-input"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                        />
                    </div>
                    <button type="submit" disabled={changingPassword}>
                        {changingPassword ? 'Changing...' : 'Change Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};
